import {
  ApiError,
  apiService,
  AmazonUser,
  Token,
  UserAgent,
  AmazonBillingInfo,
  amazonLogin as apiAmazonLogin,
} from "@blacknut/javascript-sdk/dist";
import { logD, logE, logI, logW } from "@blacknut/logging/dist";
import {
  amazonLogin,
  amazonSignup,
  amazonSubscribe,
  amazonSubscribeUpdate,
  dismissAmazonLoginError,
  dismissAmazonSubscribeError,
  dismissAmazonSubscribeUpdateError,
} from "core";
import { Dispatch } from "redux";
import { firstValueFrom } from "rxjs";
import {
  ERROR_CODE_TIMEOUT,
  FullScreenChangeListener,
  NativeModule,
  PlayOpts,
} from "./NativeModule";
import { WebRTCStopReason } from "@blacknut/playerwebrtc-sdk";
const NAMESPACE = "__android_";
const LOGGING_TAG = "Android";
export const DEBUG_AMAZON = false;

export class AndroidNativeModule implements NativeModule {
  private callbacks: Record<
    string,
    { resolve: (data: any) => void; reject: (e: any) => void }
  > = {};
  private static id = 0;
  private fullscreenChangeListeners: FullScreenChangeListener[] = [];

  private cachedDeviceInfo?: UserAgent;

  /**
   * @deprecated, use  send
   * TO be updated also on the native part
   */
  private sendMessage(message: string, payload: any) {
    logI(LOGGING_TAG, "@deprecated sendMessage");
    (window as any)["ReactNativeWebView"]?.postMessage(
      JSON.stringify({ message, payload }),
    );
  }

  constructor() {
    window.addEventListener("message", this._onMessageLegacy);
    (window as any)["ReactNativeWebView"]!.onmessage = this._onMessageNew.bind(this);
    logD(LOGGING_TAG, "Using android native module");
  }

  tearDown() {
    AndroidNativeModule.id = 0;
    this.callbacks = {};
    window.removeEventListener("message", this._onMessageLegacy);
  }

  private _onMessageNew = (e: any) => {
    this._onMessage(e);
  };
  private _onMessageLegacy = (e: MessageEvent<any>) => {
    logW(LOGGING_TAG, "@deprecated _onMessageLegacy");
    this._onMessage(e);
  };

  private _onMessage = (e: MessageEvent<any>) => {
    if (typeof e.data !== "string") {
      return;
    }

    logI(LOGGING_TAG, "got message back: %o", e.data);
    try {
      const oData = JSON.parse(e.data);
      if (oData.message && oData.message.startsWith(NAMESPACE)) {
        if (oData.id in this.callbacks) {
          if (oData.status === 0) {
            // success
            this.callbacks[oData.id].resolve(oData.payload);
          } else {
            this.callbacks[oData.id].reject({
              message: oData.error,
              code: oData.errorCode,
            });
          }
          delete this.callbacks[oData.id];
        } else {
          const s = (oData.message as string).substring(NAMESPACE.length);
          switch (s) {
            case "immersiveStateChange": {
              const { immersive } = oData.payload;
              logD(
                LOGGING_TAG,
                "Dispatch fullscreen %o to %o",
                immersive,
                this.fullscreenChangeListeners,
              );
              for (const e of this.fullscreenChangeListeners) {
                e(immersive);
              }
              break;
            }
            default: {
              logI(LOGGING_TAG, "No callback for %o", s);
            }
          }
        }
      }
    } catch (e) {
      logE(LOGGING_TAG, "Caught error parsing json: %o", e.data);
    }
  };

  private sendSync<T>(message: string, payload?: any, timeout = 5000) {
    AndroidNativeModule.id++;

    return new Promise<T>((resolve, reject) => {
      const timer =
        timeout === -1
          ? undefined
          : setTimeout(() => {
              reject({
                code: ERROR_CODE_TIMEOUT,
                message: `sendSync promise (${message}) timed out after ${timeout} ms`,
              });
            }, timeout);

      const customResolve = (data: any) => {
        logI(LOGGING_TAG, "in Custom resolve: %o", data);
        if (timer !== undefined) {
          clearTimeout(timer);
        }
        resolve(data);
      };
      const customReject = (e: any) => {
        if (timer !== undefined) {
          clearTimeout(timer);
        }
        logI(LOGGING_TAG, "in Custom reject: %o", e);
        reject(e);
      };

      const data = JSON.stringify({
        message: `${NAMESPACE}${message}`,
        payload,
        id: AndroidNativeModule.id,
      });
      logI(LOGGING_TAG, "SENDING DATA:%o ", data);
      (window as any)["ReactNativeWebView"]?.postMessage(data);
      this.callbacks[`${AndroidNativeModule.id}`] = {
        resolve: customResolve,
        reject: customReject,
      };
    });
  }

  private send(message: string, payload?: any) {
    AndroidNativeModule.id++;

    const data = JSON.stringify({
      message: `${NAMESPACE}${message}`,
      payload,
      id: AndroidNativeModule.id,
    });
    logD(LOGGING_TAG, "SENDING DATA:%o ", data);
    (window as any)["ReactNativeWebView"]?.postMessage(data);
  }

  exit() {
    this.send("exit");
  }

  setStartUrl(url: string) {
    this.sendSync("setStartUrl", url);
  }

  requestFullscreen(): Promise<void> {
    this.send("setImmersiveMode", { immersive: true });
    return Promise.resolve();
  }

  exitFullscreen() {
    this.send("setImmersiveMode", { immersive: false });
    return Promise.resolve();
  }

  availablePlayers() {
    return this.sendSync<{ label: string; value: string }[]>("getAvailablePlayers");
  }

  getPlayer() {
    return this.sendSync<string>("getPlayer");
  }

  setPlayer(s: string | undefined) {
    return this.sendSync<void>("setPlayer", s);
  }

  play(opts: PlayOpts) {
    return this.sendSync<WebRTCStopReason | undefined>("play", opts, -1);
  }

  async setUserToken(token: Token | undefined) {
    try {
      const shouldCall = !(await this.isVersionAbove("5.0.0"));
      if (shouldCall) {
        this.send("setUserToken", token);
      }
    } catch (e) {
      logE(LOGGING_TAG, "Caught error getting android version: %o", e);
      this.send("setUserToken", token);
    }
  }

  async setFamilyToken(token: Token | undefined) {
    try {
      const shouldCall = !(await this.isVersionAbove("5.0.0"));
      if (shouldCall) {
        this.send("setFamilyToken", token);
      }
    } catch (e) {
      logE(LOGGING_TAG, "Caught error getting android version: %o", e);
      this.send("setFamilyToken", token);
    }
  }

  handleInAppWebRTC() {
    return true;
  }

  getPlayerVersion(s: string) {
    return this.sendSync<string>("getPlayerVersion", { type: s });
  }

  async getDeviceInfo() {
    if (this.cachedDeviceInfo) {
      return Promise.resolve(this.cachedDeviceInfo);
    }
    this.cachedDeviceInfo = await this.sendSync<UserAgent>("getDeviceInfo");
    return Promise.resolve(this.cachedDeviceInfo);
  }

  addFullcreenChangeListener(callback: (fullscreen: boolean) => void) {
    this.fullscreenChangeListeners.push(callback);
  }

  removeFullscreenChangeListener(callback: FullScreenChangeListener) {
    this.fullscreenChangeListeners = this.fullscreenChangeListeners.filter(
      (c) => c !== callback,
    );
  }

  isFullscreen() {
    return this.sendSync<{ immersive: boolean }>("isImmersive").then(
      (res) => res.immersive,
    );
  }

  async handleInAppSignin(dispatch?: Dispatch) {
    const data = await this.sendSync<AmazonUser>(
      "amazonSignin",
      undefined,
      5 * 60 * 1000,
    );
    if (!data) {
      logE(LOGGING_TAG, "No data returned from amazon");
      return Promise.reject("No data returned from amazon");
    }
    if (dispatch) {
      try {
        const { token } = await amazonLogin(data)(dispatch);
        return token;
      } catch (e) {
        dismissAmazonLoginError()(dispatch);
        const _e = e as ApiError;
        throw { code: _e.code, message: _e.toString() };
      }
    } else {
      return firstValueFrom(
        apiAmazonLogin({ ...data.userInfo, accessToken: data.accessToken }),
      );
    }
  }

  canHandleInAppSignup() {
    return apiService.userAgent.os === "Fireos"; //FIXME check also version
  }

  canHandleInAppSignin() {
    return this.canHandleInAppSignup();
  }

  async handleInAppSignup(dispatch: Dispatch) {
    const data = await this.sendSync<AmazonUser>(
      "amazonSignin",
      undefined,
      5 * 60 * 1000,
    );
    try {
      return (await amazonSignup(data)(dispatch)).token;
    } catch (e) {
      dismissAmazonLoginError()(dispatch);
      logE(LOGGING_TAG, "Caught error on amazon signup: %o", e);
      const _e = e as ApiError;
      throw { code: _e.code, message: _e.toString() };
    }
  }

  canHandleInAppSubscribe() {
    return this.isVersionAbove("5.0.17");
  }

  async handleInAppSubscribe(dispatch: Dispatch, migrateAmazonPay = false) {
    // proceed on native part
    const { type, value: subData } = await this.sendSync<{
      type: "subscribe" | "update";
      value: unknown;
    }>(
      "amazonSubscribe",
      {
        sku: "com.blacknut.client.subscription.basic_no_trial",
      },
      5 * 60 * 1000,
    );
    logI(LOGGING_TAG, "after amazonSubscribe");

    if (migrateAmazonPay) {
      // WS will handle the notification
      return Promise.resolve([]);
    }

    //notify FB
    switch (type) {
      case "subscribe": {
        try {
          const billingInfo = await amazonSubscribe(subData)(dispatch);
          return billingInfo;
        } catch (e) {
          logE(LOGGING_TAG, "Caught error on amazon subscribe: %o", e);
          dismissAmazonSubscribeError()(dispatch);
          throw { code: "SUBSCRIBE_ERROR", message: e.toString() };
        }
      }
      case "update": {
        try {
          const billingInfo = await amazonSubscribeUpdate(subData)(dispatch);
          return billingInfo;
        } catch (e) {
          logE(LOGGING_TAG, "Caught error on amazon subscribe: %o", e);
          dismissAmazonSubscribeUpdateError()(dispatch);
          throw { code: "SUBSCRIBE_ERROR", message: e.toString() };
        }
      }
      default:
        throw { code: "RUNTIME_ERROR", message: "Invalid type" };
    }
  }

  private async getAndroidVersion() {
    const deviceInfo = await this.getDeviceInfo();
    const matches = deviceInfo.clientVersion.match(/^([0-9]+)\.([0-9]+)\.([0-9]+)/);
    if (matches) {
      return `${matches[1]}.${matches[2]}.${matches[3]}`;
    }
    return undefined;
  }

  private async isVersionAbove(version: string) {
    const androidVersion = await this.getAndroidVersion();
    logD(LOGGING_TAG, "Android version: %o", androidVersion);

    if (!androidVersion) {
      return Promise.reject({ message: "Unable to parse android version" });
    }
    return (
      androidVersion.localeCompare(version, undefined, {
        numeric: true,
        sensitivity: "base",
      }) >= 0
    );
  }

  canHandleAISOnboarding() {
    return this.isVersionAbove("4.9.12");
  }

  handleAISOnboarding() {
    this.send("handleAISOnboarding");
  }

  checkForUpdates() {
    this.send("checkForUpdates");
  }

  async getDefaultTheme() {
    try {
      const shouldCall = !(await this.isVersionAbove("5.0.0"));
      if (shouldCall) {
        return this.sendSync<string>("getDefaultTheme");
      }
      return Promise.resolve("dark");
    } catch (e) {
      logE(LOGGING_TAG, "Caught error getting android version: %o", e);
      return this.sendSync<string>("getDefaultTheme");
    }
  }

  getReferrer() {
    return this.sendSync<string>("getReferrer");
  }

  getInstallReferrer() {
    return this.sendSync<string>("getInstallReferrer");
  }

  getAmazonBillingInfo() {
    return this.sendSync<AmazonBillingInfo>("amazonGetPurchases", undefined, -1);
  }

  /**
   * FIX for https://blacknut.atlassian.net/browse/CA-5989
   * On android new version the Click & ther press enter is called (which is not the case on the web version & on legacy RN version)
   */
  async removeOnPressEnter() {
    return await !this.isVersionAbove("5.0.0");
  }
}
